import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TagPill } from '../../schemas/api/search/category-tags';

interface CategoryTagPillsProps {
  /** null while loading: the row holds its height and shows placeholders. */
  pills: TagPill[] | null;
}

/** Placeholder pills while the real ones load, at roughly real widths. */
const PLACEHOLDER_COUNT = 8;

/** Pixels of slack before a side counts as having more to show. */
const EDGE_SLACK = 8;

function Chevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points={direction === 'right' ? '9 6 15 12 9 18' : '15 6 9 12 15 18'} />
    </svg>
  );
}

/**
 * Related tags on a browse page, one line, each linking to /shop/<tag>. Pages
 * sideways with chevrons the way the category nav bar does
 * (components/nav-category-bar.tsx).
 */
export default function CategoryTagPills({ pills }: CategoryTagPillsProps) {
  const scrollerRef = useRef<HTMLUListElement>(null);
  const [more, setMore] = useState({ left: false, right: false });

  // Only called by the effect below, which adds and removes it with the list.
  const updateChevrons = useCallback(() => {
    const el = scrollerRef.current as HTMLUListElement;
    setMore({
      left: el.scrollLeft > EDGE_SLACK,
      right: el.scrollWidth - el.clientWidth - el.scrollLeft > EDGE_SLACK,
    });
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateChevrons();
    el.addEventListener('scroll', updateChevrons, { passive: true });
    window.addEventListener('resize', updateChevrons);
    return () => {
      el.removeEventListener('scroll', updateChevrons);
      window.removeEventListener('resize', updateChevrons);
    };
  }, [updateChevrons, pills]);

  // The chevrons are drawn beside the list, so the ref is set.
  const page = (direction: 1 | -1) => {
    const el = scrollerRef.current as HTMLUListElement;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  // The row's height is fixed in CSS and held whether the pills are loading,
  // loaded or absent, so the listings under it never move when they arrive.
  if (pills === null) {
    return (
      <div className="category-tag-pills" aria-busy="true" aria-label="Loading related tags" data-testid="category-tag-pills-loading">
        <ul className="category-tag-pills-list" aria-hidden="true">
          {Array.from({ length: PLACEHOLDER_COUNT }, (_, i) => (
            <li key={i}>
              <span className="category-tag-pill category-tag-pill--placeholder skeleton" />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (pills.length === 0) {
    return <div className="category-tag-pills" aria-hidden="true" data-testid="category-tag-pills-empty" />;
  }
  return (
    <nav aria-label="Related tags" className="category-tag-pills">
      <ul ref={scrollerRef} className="category-tag-pills-list">
        {pills.map(pill => (
          <li key={pill.tag}>
            <Link href={pill.href} className="category-tag-pill">
              {pill.label ?? pill.tag}
            </Link>
          </li>
        ))}
      </ul>
      {more.left && (
        <button type="button" className="category-tag-pills-chevron left" aria-label="Scroll tags left" onClick={() => page(-1)}>
          <Chevron direction="left" />
        </button>
      )}
      {more.right && (
        <button type="button" className="category-tag-pills-chevron right" aria-label="Scroll tags right" onClick={() => page(1)}>
          <Chevron direction="right" />
        </button>
      )}
    </nav>
  );
}
